import axios from 'axios';
import { useCallback, useSyncExternalStore } from 'react';

const CACHE_TTL_MS = 5 * 60 * 1000;
const records = new Map();

const emptySnapshot = {
    driverHistory: [],
    defaults: {},
    baseRevenue: null,
    netDebt: null,
    shares: null,
    price: null,
    marketCap: null,
    loading: false,
    error: null,
};

function createRecord() {
    return {
        data: null,
        error: null,
        loading: false,
        promise: null,
        updatedAt: 0,
        snapshot: emptySnapshot,
        listeners: new Set(),
    };
}

function getKey(apiUrl, ticker) {
    if (!apiUrl || !ticker) return null;
    return `${apiUrl}|${ticker.toUpperCase()}`;
}

function getRecord(key) {
    if (!records.has(key)) records.set(key, createRecord());
    return records.get(key);
}

function updateSnapshot(record, nextState) {
    Object.assign(record, nextState);
    const payload = record.data || {};
    record.snapshot = {
        driverHistory: payload.history || [],
        defaults: payload.suggested_defaults || {},
        baseRevenue: payload.base_revenue || null,
        netDebt: payload.net_debt || null,
        debt: payload.net_debt?.debt || null,
        cash: payload.net_debt?.cash || null,
        shares: payload.shares_outstanding || null,
        price: payload.current_price || null,
        marketCap: payload.market_cap ?? null,
        loading: record.loading,
        error: record.error,
    };
    record.listeners.forEach((listener) => listener());
}

function load(apiUrl, ticker, record) {
    const cacheIsFresh = record.data && Date.now() - record.updatedAt < CACHE_TTL_MS;
    if (record.promise || cacheIsFresh) return;

    updateSnapshot(record, { loading: true, error: null });
    record.promise = axios
        .get(`${apiUrl}/api/dcf-defaults?ticker=${encodeURIComponent(ticker.toUpperCase())}`)
        .then(({ data }) => {
            updateSnapshot(record, {
                data,
                loading: false,
                error: null,
                updatedAt: Date.now(),
            });
        })
        .catch((error) => {
            updateSnapshot(record, {
                loading: false,
                error: error?.response?.data?.error || error.message || 'Unable to load DCF data.',
            });
        })
        .finally(() => {
            record.promise = null;
        });
}

function subscribe(apiUrl, ticker, listener) {
    const key = getKey(apiUrl, ticker);
    if (!key) return () => {};

    const record = getRecord(key);
    record.listeners.add(listener);
    load(apiUrl, ticker, record);
    return () => record.listeners.delete(listener);
}

function getSnapshot(apiUrl, ticker) {
    const key = getKey(apiUrl, ticker);
    return key ? getRecord(key).snapshot : emptySnapshot;
}

export function useDcfWorkbenchData(ticker) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const subscribeToData = useCallback(
        (listener) => subscribe(apiUrl, ticker, listener),
        [apiUrl, ticker],
    );
    const readSnapshot = useCallback(
        () => getSnapshot(apiUrl, ticker),
        [apiUrl, ticker],
    );

    return useSyncExternalStore(subscribeToData, readSnapshot, readSnapshot);
}

export function clearDcfWorkbenchDataCache() {
    records.clear();
}
